import {
  GroupFormDTO,
  GroupListFilterDTO,
  GroupSearchOption,
} from '../groups/types';
import ApiService from '../global/services/api.service';
import QueryBuilder from '../shared/utils/query-builder';
import { ListDTO, MatchingMethod } from '../global';
import {
  EntityDTO,
  EntityType,
  VehicleLightDTO,
  WorkingSchedule,
} from '../shared/types';
import { LocationLightDTO } from './location.service';

export interface GroupLightDTO extends EntityDTO {
  description: string;
  id: number;
  transports: VehicleLightDTO[];
  locations: LocationLightDTO[];
  color: string;
  name: string;
  type: EntityType;
}

export interface GroupDTO extends GroupLightDTO {
  courierSchedule: WorkingSchedule[];
  managerSchedule: WorkingSchedule[];
}

class GroupService {
  static ENDPOINT = '/groups';

  static getGroups({
    search,
    limit,
    offset,
    color,
    transports,
    includeLocations,
    couriers,
  }: GroupListFilterDTO) {
    const body = new QueryBuilder()
      .addLimit(limit || 20)
      .addOffset(offset || 0)
      .or({
        field: GroupSearchOption.NAME,
        value: search,
        matchingMethod: MatchingMethod.CONTAINS,
      })
      .and({
        value: color?.length === 1 ? color[0] : undefined,
        values: color && color.length > 1 ? color : undefined,
        field: GroupSearchOption.COLOR,
        matchingMethod:
          color?.length === 1 ? MatchingMethod.EXACT : MatchingMethod.CONTAINS,
      })

      .and({
        value: transports?.length === 1 ? transports[0] : undefined,
        values: transports && transports.length > 1 ? transports : undefined,
        field: GroupSearchOption.TRANSPORTS,
        matchingMethod:
          transports?.length === 1
            ? MatchingMethod.EXACT
            : MatchingMethod.CONTAINS,
      })
      .and({
        value: couriers?.length === 1 ? couriers[0] : undefined,
        values: couriers && couriers.length > 1 ? couriers : undefined,
        field: GroupSearchOption.COURIERS,
        matchingMethod:
          couriers?.length === 1
            ? MatchingMethod.EXACT
            : MatchingMethod.CONTAINS,
      })
      .or({
        field: GroupSearchOption.DRIVER_FIRST_NAME,
        value: search,
        matchingMethod: MatchingMethod.CONTAINS,
      })
      .or({
        field: GroupSearchOption.DRIVER_LAST_NAME,
        value: search,
        matchingMethod: MatchingMethod.CONTAINS,
      })
      .or({
        field: GroupSearchOption.DESCRIPTION,
        value: search,
        matchingMethod: MatchingMethod.CONTAINS,
      });

    let endpoint = `${GroupService.ENDPOINT}/search`;

    if (includeLocations) {
      endpoint += '?includeLocations=true';
    }

    return ApiService.post<ListDTO<GroupLightDTO>>(endpoint, body, {
      notifyErrors: false,
    });
  }

  static createGroup(group: GroupFormDTO) {
    const data = GroupService.getGroupRequestPayload(group);
    return ApiService.post(GroupService.ENDPOINT, data);
  }

  static updateGroup(group: GroupFormDTO, id: string | number) {
    const data = GroupService.getGroupRequestPayload(group);
    return ApiService.patch(`${GroupService.ENDPOINT}/${id}`, data);
  }

  static getGroup(id: number | string) {
    return ApiService.get<GroupDTO>(`${GroupService.ENDPOINT}/${id}`);
  }

  static deleteGroup(id: number | string) {
    return ApiService.delete(`${GroupService.ENDPOINT}/${id}`);
  }

  static getGroupRequestPayload(group: GroupFormDTO) {
    return {
      name: group.name,
      color: group.color,
      transportIds: group.transports.map((el) => el.id),
      description: group.description,
      locationIds: group.locations.value.map((el) => el.id),
    };
  }
}

export default GroupService;
